import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import { CatiaCollabEvent } from '~/entities/container/conversation/interfaces/events'

import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

type ConversationId = IConversation['id']

/**
 * Stores the most recent ids per conversation for
 * (1) Co Review
 * (2) Design Share
 * (3) Snapshot
 */
export const useMostRecentCollabActions = createGlobalState(() => {
  const mapConversationCoReview = reactive<Map<ConversationId, SUBJECT_6W_URI>>(new Map())
  const activeEventsSet = reactive<Set<SUBJECT_6W_URI>>(new Set())
  const lastSetConversation = ref<SUBJECT_6W_URI>('')

  /**
   * Method that updates the most recent co-review id
   *
   * @param conversationId The "id" of the conversation
   * @param coReviewId The id of the message
   */
  const pushRecentCoReview = (conversationId: ConversationId, coReviewId: SUBJECT_6W_URI) => {
    mapConversationCoReview.set(conversationId, coReviewId)
  }

  /**
   * Method that updates the most recent co-review id
   *
   * @param conversationId The "id" of the conversation
   */
  const resetRecentCoReview = (conversationId: ConversationId) => {
    mapConversationCoReview.delete(conversationId)
  }

  /**
   * Adds an event id to the set
   *
   * @param modelId The id of the event
   */
  const addActiveEventToSet = (modelId: SUBJECT_6W_URI) => {
    activeEventsSet.add(modelId)
  }

  /**
   * Updates the most recent collab id based on the action type
   *
   * @param params required parameters to update the actions
   * @param params.coReviewEvent The collab event to persist OR any other action (it will not be persisted in that case)
   * @param params.conversationId The "id" of the conversation
   * @param params.messageId The message id
   */
  const updateRecentCollabAction = (params: {
    coReviewEvent: CatiaCollabEvent | string
    conversationId: string
    messageId: string
  }) => {
    const {
      coReviewEvent,
      conversationId,
      messageId,
    } = params

    switch (coReviewEvent) {
      case CatiaCollabEvent.CoReview:
        pushRecentCoReview(conversationId, messageId)
        break
      case CatiaCollabEvent.CoReviewLeft:
      case CatiaCollabEvent.CoReviewStop:
        resetRecentCoReview(conversationId)
        break
      case CatiaCollabEvent.DesignShare:
      case CatiaCollabEvent.SnapShot:
        activeEventsSet.add(messageId)
        break
    }
  }

  /**
   * Returns the most recent co-review id of the passed conversation
   *
   * @param conversationId The "id" of the conversation
   * @returns The most recent co-review id, if present
   */
  const getRecentCoReviewId = (conversationId: ConversationId) => mapConversationCoReview.get(conversationId)

  return {
    activeEventsSet,
    lastSetConversation,

    pushRecentCoReview,
    resetRecentCoReview,
    addActiveEventToSet,
    updateRecentCollabAction,
    getRecentCoReviewId,
  }
})
